<template>
    <section class="tk-card">
        <div class="tk-card-header tk-card-header--compact">
            <h2>Custom Fields</h2>
            <q-btn
                v-if="hasEditableFields"
                flat
                dense
                no-caps
                color="primary"
                label="Edit"
                @click="editCustomFields"
            />
        </div>

        <div v-if="loading" class="row justify-center q-pa-sm">
            <q-spinner color="primary" size="24px" />
        </div>
        <div v-else-if="error" class="text-negative text-caption">
            {{ error }}
            <q-btn flat dense no-caps color="primary" label="Retry" @click="loadCustomFields" />
        </div>
        <div v-else-if="!fields.length" class="text-grey-7">
            No custom fields configured.
        </div>
        <div v-else class="tk-custom-field-list">
            <div v-for="field in fields" :key="field.id" class="tk-custom-field-value">
                <div class="row items-center q-gutter-xs">
                    <span>{{ field.title }}</span>
                    <q-badge v-if="field.is_historical" outline color="grey-7" label="Historical" />
                </div>
                <strong>{{ displayValue(field) }}</strong>
                <small v-if="field.description">{{ field.description }}</small>
            </div>
        </div>
    </section>

    <TkRightDrawer
        v-model="showEditor"
        title="Custom Fields"
        subtitle="Update the additional information for this Client Engagement."
        width="680px"
    >
        <q-form ref="customFieldsForm" @submit.prevent="saveCustomFields">
            <q-card-section>
                <div v-for="field in editableFields" :key="field.id" class="tk-custom-field-input">
                    <p><strong>{{ field.title }}</strong></p>
                    <p v-if="field.description" class="text-grey-7 text-caption">
                        {{ field.description }}
                    </p>

                    <q-input
                        v-if="field.field_type === 'TEXT_LINE'"
                        v-model="responses[field.id]"
                        outlined
                        dense
                        maxlength="500"
                    />
                    <q-input
                        v-else-if="field.field_type === 'TEXT_AREA'"
                        v-model="responses[field.id]"
                        outlined
                        dense
                        autogrow
                        type="textarea"
                        maxlength="4000"
                    />
                    <TkDateField
                        v-else-if="field.field_type === 'DATE'"
                        v-model="responses[field.id]"
                        :rules="dateRules"
                    />
                    <q-input
                        v-else-if="field.field_type === 'NUMBER'"
                        v-model.number="responses[field.id]"
                        outlined
                        dense
                        type="number"
                        step="any"
                    />
                    <q-select
                        v-else-if="field.field_type === 'YES_NO'"
                        v-model="responses[field.id]"
                        :options="yesNoOptions"
                        outlined
                        dense
                        clearable
                        emit-value
                        map-options
                    />
                </div>

                <template v-if="historicalFields.length">
                    <q-separator class="q-my-lg" />
                    <p class="text-grey-7 text-caption">
                        Historical responses are read-only.
                    </p>
                    <div v-for="field in historicalFields" :key="field.id" class="tk-custom-field-value q-mt-md">
                        <span>{{ field.title }}</span>
                        <strong>{{ displayValue(field) }}</strong>
                    </div>
                </template>
            </q-card-section>

            <q-card-actions align="right">
                <q-btn flat no-caps color="negative" label="Cancel" v-close-popup />
                <q-btn
                    unelevated
                    no-caps
                    color="primary"
                    icon="save"
                    label="Save"
                    type="submit"
                    :loading="saving"
                />
            </q-card-actions>
        </q-form>
    </TkRightDrawer>
</template>

<script>
import api from "@/services/api/api";
import notify from "@/services/util/notify";
import TkDateField from "@/components/TkClients/TkDateField.vue";
import TkRightDrawer from "@/components/TkClients/TkRightDrawer.vue";
import {
    customFieldDateForApi,
    customFieldDateForDisplay,
    customFieldDisplayValue,
    isValidCustomFieldDate,
} from "@/services/util/customFields";

export default {
    name: "TkEngagementCustomFieldsPanel",
    components: {
        TkDateField,
        TkRightDrawer,
    },
    props: {
        personId: {
            type: [Number, String],
            required: true,
        },
        engagementId: {
            type: [Number, String],
            required: true,
        },
    },
    data() {
        return {
            loading: false,
            saving: false,
            error: null,
            fields: [],
            responses: {},
            showEditor: false,
            yesNoOptions: [
                { label: "Yes", value: true },
                { label: "No", value: false },
            ],
        };
    },
    computed: {
        editableFields() {
            return this.fields.filter(field => field.is_editable);
        },
        historicalFields() {
            return this.fields.filter(field => field.is_historical);
        },
        hasEditableFields() {
            return this.editableFields.length > 0;
        },
        dateRules() {
            return [value => isValidCustomFieldDate(value) || "Enter a valid date."];
        },
    },
    async mounted() {
        await this.loadCustomFields();
    },
    methods: {
        displayValue(field) {
            return customFieldDisplayValue(field);
        },
        editCustomFields() {
            this.responses = this.editableFields.reduce((values, field) => ({
                ...values,
                [field.id]: field.field_type === "DATE"
                    ? customFieldDateForDisplay(field.response)
                    : field.response,
            }), {});
            this.showEditor = true;
        },
        responseForApi(field) {
            const value = this.responses[field.id];
            if (field.field_type === "DATE") return customFieldDateForApi(value);
            return value === "" || value === undefined ? null : value;
        },
        async saveCustomFields() {
            const isValid = await this.$refs.customFieldsForm.validate();
            if (!isValid) return;

            this.saving = true;
            try {
                const responses = this.editableFields.map(field => ({
                    custom_field_id: field.id,
                    response: this.responseForApi(field),
                }));
                const response = await api.tkPeople.updateEngagementCustomFields(
                    this.personId,
                    this.engagementId,
                    responses
                );
                this.fields = response.data || [];
                this.showEditor = false;
                notify.primary("Custom fields saved", "top");
            } catch (error) {
                const message = error?.response?.statusText;
                notify.error(
                    message && message !== "Internal Server Error" ? message : "Failed to save custom fields",
                    "top"
                );
            } finally {
                this.saving = false;
            }
        },
        async loadCustomFields() {
            this.loading = true;
            this.error = null;
            try {
                const response = await api.tkPeople.getEngagementCustomFields(
                    this.personId,
                    this.engagementId
                );
                this.fields = response.data || [];
            } catch {
                this.error = "Failed to load custom fields.";
            } finally {
                this.loading = false;
            }
        },
    },
};
</script>

<style scoped>
.tk-custom-field-list {
    display: grid;
    gap: 14px;
}

.tk-custom-field-value {
    display: grid;
    gap: 3px;
}

.tk-custom-field-value > div > span,
.tk-custom-field-value small {
    color: #66736d;
}

.tk-custom-field-value strong {
    overflow-wrap: anywhere;
    white-space: pre-wrap;
}

.tk-custom-field-input + .tk-custom-field-input {
    margin-top: 20px;
}

.tk-custom-field-input > p {
    margin: 0 0 6px;
}
</style>
